import React from 'react';
import {
  Box,
  Paper,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  LinearProgress,
  Stack,
  Typography,
  SxProps,
  Theme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  SortingState,
  TableState,
  useReactTable,
} from '@tanstack/react-table';

type TableColumnMeta = {
  align?: TableCellProps['align'];
  cellSx?: SxProps<Theme>;
  headerSx?: SxProps<Theme>;
};

export type TanstackTableProps<TData extends object> = {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  isLoading?: boolean;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  toolbarActions?: React.ReactNode;
  enableGlobalFilter?: boolean;
  globalFilterPlaceholder?: string;
  rowsPerPageOptions?: number[];
  showPagination?: boolean;
  stickyHeader?: boolean;
  renderRowActions?: (row: Row<TData>) => React.ReactNode;
  actionsColumnLabel?: React.ReactNode;
  emptyStateText?: string;
  rowSx?: (row: Row<TData>) => SxProps<Theme>;
  paperSx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  initialState?: Partial<TableState>;
  onRowClick?: (row: Row<TData>) => void;
  getRowId?: (row: TData, index: number) => string;
};

export function TanstackTable<TData extends object>({
  data,
  columns,
  isLoading = false,
  title,
  subtitle,
  toolbarActions,
  enableGlobalFilter = false,
  globalFilterPlaceholder = 'Pesquisar',
  rowsPerPageOptions = [5, 10, 25, 50],
  showPagination = true,
  stickyHeader = false,
  renderRowActions,
  actionsColumnLabel = 'Ações',
  emptyStateText = 'Nenhum registro para exibir',
  rowSx,
  paperSx,
  containerSx,
  initialState,
  onRowClick,
  getRowId,
}: TanstackTableProps<TData>) {
  const initialPagination = initialState?.pagination ?? {
    pageIndex: 0,
    pageSize: rowsPerPageOptions[2] ?? 10,
  };
  const [sorting, setSorting] = React.useState<SortingState>(initialState?.sorting ?? []);
  const [globalFilter, setGlobalFilter] = React.useState(
    (initialState?.globalFilter as string) ?? '',
  );
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: initialPagination.pageIndex ?? 0,
    pageSize: initialPagination.pageSize ?? rowsPerPageOptions[2] ?? 10,
  });

  const columnsWithRowNumber = React.useMemo<ColumnDef<TData, any>[]>(() => {
    const rowNumberColumn: ColumnDef<TData, number> = {
      id: '__rowNumber',
      header: '#',
      enableSorting: false,
      cell: ({ row, table: tableInstance }) => {
        const pageIndex = tableInstance.getState().pagination.pageIndex ?? 0;
        const pageSize = tableInstance.getState().pagination.pageSize ?? 0;
        const visibleRows = tableInstance.getRowModel().rows;
        const rowIndex = visibleRows.findIndex((visibleRow) => visibleRow.id === row.id);
        if (rowIndex < 0) {
          return '';
        }
        return rowIndex + 1 + pageIndex * pageSize;
      },
      meta: {
        align: 'left',
        headerSx: { width: '5px' },
        cellSx: { width: '5px' },
      },
    };

    return [rowNumberColumn, ...columns];
  }, [columns]);

  const table = useReactTable({
    data,
    columns: columnsWithRowNumber,
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState,
    getRowId,
  });

  const headerGroups = table.getHeaderGroups();
  const headerGroupsToRender = React.useMemo(() => {
    const seenSignatures = new Set<string>();
    return headerGroups.filter((group) => {
      const signature = group.headers.map((header) => header.id).join('-');
      if (seenSignatures.has(signature)) {
        return false;
      }
      seenSignatures.add(signature);
      return true;
    });
  }, [headerGroups]);
  const hasActions = Boolean(renderRowActions);
  const bodyColumnCount = Math.max(table.getVisibleLeafColumns().length + (hasActions ? 1 : 0), 1);

  return (
    <Paper variant="outlined" sx={{ width: '100%', ...paperSx }}>
      {(title || subtitle || enableGlobalFilter || toolbarActions) && (
        <Box px={2} py={1} display="flex" flexWrap="wrap" gap={1} alignItems="center">
          <Box flex="1 1 auto" minWidth={0}>
            {title && (
              <Typography variant="subtitle1" component="div" fontWeight={600}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {(enableGlobalFilter || toolbarActions) && (
            <Stack direction="row" spacing={1} alignItems="center">
              {enableGlobalFilter && (
                <TextField
                  size="small"
                  placeholder={globalFilterPlaceholder}
                  value={globalFilter ?? ''}
                  onChange={(event) => setGlobalFilter(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {toolbarActions}
            </Stack>
          )}
        </Box>
      )}
      {isLoading && <LinearProgress />}
      <TableContainer sx={{ width: '100%', ...containerSx }}>
        <MuiTable size="small" stickyHeader={stickyHeader}>
          <TableHead>
            {headerGroupsToRender.map((headerGroup, headerGroupIndex) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const isSorted = header.column.getIsSorted();
                  const headerMeta = header.column.columnDef.meta as TableColumnMeta | undefined;

                  return (
                    <TableCell
                      key={header.id}
                      colSpan={header.colSpan}
                      sortDirection={typeof isSorted === 'string' ? isSorted : false}
                      align={headerMeta?.align}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        textTransform: 'none',
                        ...(headerMeta?.headerSx ?? {}),
                      }}
                    >
                      {canSort ? (
                        <TableSortLabel
                          active={isSorted !== false}
                          direction={isSorted === 'desc' ? 'desc' : 'asc'}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          disabled={!canSort}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </TableSortLabel>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableCell>
                  );
                })}
                {hasActions && headerGroupIndex === 0 && (
                  <TableCell
                    align="center"
                    rowSpan={headerGroupsToRender.length}
                    sx={{ width: '110px', fontWeight: 600, fontSize: '0.85rem' }}
                  >
                    {actionsColumnLabel}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover={Boolean(onRowClick)}
                onClick={() => onRowClick?.(row)}
                sx={[
                  { cursor: onRowClick ? 'pointer' : undefined },
                  ...(Array.isArray(rowSx) ? rowSx : [rowSx]).map((sxFn) =>
                    sxFn ? sxFn(row) : {},
                  ),
                ]}
              >
                {row.getVisibleCells().map((cell) => {
                  const cellMeta = cell.column.columnDef.meta as TableColumnMeta | undefined;
                  return (
                    <TableCell
                      key={cell.id}
                      align={cellMeta?.align}
                      sx={cellMeta?.cellSx}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  );
                })}
                {renderRowActions && (
                  <TableCell align="center">{renderRowActions(row)}</TableCell>
                )}
              </TableRow>
            ))}
            {table.getRowModel().rows.length === 0 && (
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={bodyColumnCount}
                >
                  <Typography variant="body2" color="text.secondary">
                    {isLoading ? 'Carregando...' : emptyStateText}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {showPagination && (
            <TableFooter>
              <TableRow>
                <TableCell align="right" colSpan={bodyColumnCount}>
                  <TablePagination
                    component="div"
                    count={table.getFilteredRowModel().rows.length}
                    page={table.getState().pagination.pageIndex}
                    rowsPerPage={table.getState().pagination.pageSize}
                    onPageChange={(event, newPage) => table.setPageIndex(newPage)}
                    onRowsPerPageChange={(event) =>
                      table.setPageSize(Number(event.target.value))
                    }
                    rowsPerPageOptions={rowsPerPageOptions}
                    labelRowsPerPage="Linhas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                  />
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </MuiTable>
      </TableContainer>
    </Paper>
  );
}
